import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Eye, Heart, Share2, Flag, MessageCircle, Star, ChevronLeft, CreditCard, Banknote, Zap, ShieldCheck, Copy, Check, Calendar, Tag, Briefcase, Search as SearchIcon, Home, Car, Phone, ExternalLink, Mail } from 'lucide-react';
import { listingsAPI, messagesAPI, reportsAPI, recentlyViewedAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

const PLACEHOLDER_IMG = "https://images.pexels.com/photos/8135269/pexels-photo-8135269.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function DetailRow({ icon: Icon, label, value, href }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      {Icon && <Icon className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{label}</p>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm text-[#002FA7] font-medium hover:underline flex items-center gap-1">{value} <ExternalLink className="w-3 h-3" /></a>
        ) : (
          <p className="text-sm text-zinc-800 font-medium">{value}</p>
        )}
      </div>
    </div>
  );
}

function TypeDetails({ listing }) {
  const tf = listing.type_fields || {};
  const lt = listing.listing_type;

  const titleMap = {
    garage_sale: 'Garage Sale Details', event: 'Event Details', job: 'Job Details',
    service: 'Service Details', lost_found: 'Lost & Found Details', announcement: 'Announcement Details',
    rental: 'Rental Details', vehicle: 'Vehicle Details', real_estate: 'Property Details',
  };
  const colorMap = {
    garage_sale: 'border-emerald-200 bg-emerald-50/30', event: 'border-violet-200 bg-violet-50/30',
    job: 'border-amber-200 bg-amber-50/30', service: 'border-blue-200 bg-blue-50/30',
    lost_found: 'border-red-200 bg-red-50/30', announcement: 'border-orange-200 bg-orange-50/30',
    rental: 'border-teal-200 bg-teal-50/30', vehicle: 'border-indigo-200 bg-indigo-50/30',
    real_estate: 'border-rose-200 bg-rose-50/30',
  };

  if (!titleMap[lt]) return null;

  return (
    <div className={`border rounded-lg p-5 ${colorMap[lt] || 'border-zinc-200'}`} data-testid="type-details">
      <h3 className="font-bold text-zinc-900 mb-3 text-sm">{titleMap[lt]}</h3>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {lt === 'garage_sale' && <>
          <DetailRow icon={Calendar} label="Date" value={tf.sale_date ? `${tf.sale_date}${tf.sale_end_date ? ' — ' + tf.sale_end_date : ''}` : null} />
          <DetailRow icon={Clock} label="Time" value={tf.sale_time_start ? `${tf.sale_time_start}${tf.sale_time_end ? ' — ' + tf.sale_time_end : ''}` : null} />
          <DetailRow icon={MapPin} label="Address" value={tf.sale_address} />
          <DetailRow icon={Tag} label="Directions" value={tf.sale_directions} />
        </>}
        {lt === 'event' && <>
          <DetailRow icon={Calendar} label="Date" value={tf.event_date ? `${tf.event_date}${tf.event_end_date ? ' — ' + tf.event_end_date : ''}` : null} />
          <DetailRow icon={Clock} label="Time" value={tf.event_time ? `${tf.event_time}${tf.event_end_time ? ' — ' + tf.event_end_time : ''}` : null} />
          <DetailRow icon={MapPin} label="Venue" value={tf.venue} />
          <DetailRow icon={MapPin} label="Venue Address" value={tf.venue_address} />
          <DetailRow label="Organizer" value={tf.organizer} />
          <DetailRow label="Tickets" value={tf.ticket_url} href={tf.ticket_url} />
          {(tf.is_free === true || tf.is_free === 'true') && <DetailRow label="Price" value="FREE" />}
        </>}
        {lt === 'job' && <>
          <DetailRow icon={Briefcase} label="Company" value={tf.company_name} />
          <DetailRow label="Job Type" value={tf.job_type?.replace('_', ' ')} />
          <DetailRow label="Salary Range" value={tf.salary_min || tf.salary_max ? `$${tf.salary_min || '?'} — $${tf.salary_max || '?'} ${tf.salary_period || ''}` : null} />
          <DetailRow label="Remote" value={tf.remote_option?.replace('_', ' ')} />
          <DetailRow icon={Mail} label="Apply Email" value={tf.apply_email} href={tf.apply_email ? `mailto:${tf.apply_email}` : null} />
          <DetailRow label="Apply URL" value={tf.apply_url ? 'Apply Online' : null} href={tf.apply_url} />
        </>}
        {lt === 'service' && <>
          <DetailRow icon={MapPin} label="Service Area" value={tf.service_area} />
          <DetailRow label="Hourly Rate" value={tf.hourly_rate ? `$${tf.hourly_rate}/hr` : null} />
          <DetailRow label="Experience" value={tf.experience_years ? `${tf.experience_years} years` : null} />
          <DetailRow label="Availability" value={tf.availability} />
          {(tf.license_certified === true || tf.license_certified === 'true') && <DetailRow icon={ShieldCheck} label="Certified" value="Licensed / Certified Professional" />}
        </>}
        {lt === 'lost_found' && <>
          <DetailRow icon={SearchIcon} label="Status" value={tf.lost_or_found === 'lost' ? 'LOST' : 'FOUND'} />
          <DetailRow icon={Calendar} label="Date" value={tf.date_occurred} />
          <DetailRow icon={MapPin} label="Location" value={tf.location_detail} />
          <DetailRow label="Type" value={tf.item_type} />
          <DetailRow label="Pet Name" value={tf.pet_name} />
          <DetailRow label="Breed" value={tf.pet_breed} />
          <DetailRow icon={Phone} label="Contact" value={tf.contact_phone} />
          <DetailRow label="Reward" value={tf.reward} />
        </>}
        {lt === 'rental' && <>
          <DetailRow icon={Home} label="Bedrooms" value={tf.bedrooms} />
          <DetailRow label="Bathrooms" value={tf.bathrooms} />
          <DetailRow label="Square Feet" value={tf.square_feet ? `${tf.square_feet} sq ft` : null} />
          <DetailRow icon={Calendar} label="Available" value={tf.available_date} />
          <DetailRow label="Lease Term" value={tf.lease_term?.replace(/_/g, ' ')} />
          {(tf.pet_friendly === true || tf.pet_friendly === 'true') && <DetailRow label="Pet Friendly" value="Yes" />}
          {(tf.parking === true || tf.parking === 'true') && <DetailRow label="Parking" value="Included" />}
          {(tf.laundry === true || tf.laundry === 'true') && <DetailRow label="Laundry" value="In-Unit" />}
          {(tf.utilities_included === true || tf.utilities_included === 'true') && <DetailRow label="Utilities" value="Included" />}
        </>}
        {lt === 'vehicle' && <>
          <DetailRow icon={Car} label="Vehicle" value={tf.year && tf.make && tf.model ? `${tf.year} ${tf.make} ${tf.model}` : null} />
          <DetailRow label="Mileage" value={tf.mileage ? `${Number(tf.mileage).toLocaleString()} km` : null} />
          <DetailRow label="Transmission" value={tf.transmission} />
          <DetailRow label="Fuel Type" value={tf.fuel_type} />
          <DetailRow label="Drivetrain" value={tf.drivetrain?.toUpperCase()} />
          <DetailRow label="Color" value={tf.color} />
        </>}
        {lt === 'real_estate' && <>
          <DetailRow icon={Home} label="Bedrooms" value={tf.bedrooms} />
          <DetailRow label="Bathrooms" value={tf.bathrooms} />
          <DetailRow label="Square Feet" value={tf.square_feet ? `${tf.square_feet} sq ft` : null} />
          <DetailRow label="Lot Size" value={tf.lot_size} />
          <DetailRow label="Year Built" value={tf.year_built} />
          <DetailRow label="MLS #" value={tf.mls_number} />
        </>}
      </div>
    </div>
  );
}

export default function ListingDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [selectedImg, setSelectedImg] = useState(0);
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = listing ? `Check out "${listing.title}" for $${listing.price?.toFixed(2)} on Durham Market!` : '';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
  };

  useEffect(() => {
    listingsAPI.getOne(id).then(r => { setListing(r.data); setLoading(false); }).catch(() => setLoading(false));
    if (user) recentlyViewedAPI.record(id).catch(() => {});
  }, [id, user]);

  const handleSendMessage = async () => {
    if (!user) { navigate('/auth'); return; }
    if (!message.trim()) return;
    setSendingMsg(true);
    try {
      await messagesAPI.send({ receiver_id: listing.seller_id, listing_id: listing.listing_id, content: message });
      toast.success('Message sent! Check your inbox.');
      setMessage('');
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSendingMsg(false);
    }
  };

  const handleReport = async () => {
    if (!user) { navigate('/auth'); return; }
    try {
      await reportsAPI.create({ listing_id: listing.listing_id, reason: reportReason });
      toast.success('Report submitted. Thank you for keeping the community safe.');
      setReportReason('');
    } catch {
      toast.error('Failed to submit report');
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!listing) return (
    <div className="min-h-screen flex flex-col items-center justify-center">
      <p className="text-lg text-zinc-500 mb-4">Listing not found</p>
      <Link to="/browse" className="text-[#002FA7] font-bold hover:underline">Back to browse</Link>
    </div>
  );

  const images = listing.images?.length > 0 ? listing.images : [PLACEHOLDER_IMG];
  const seller = listing.seller || {};
  const isOwner = user?.user_id === listing.seller_id;

  return (
    <div className="min-h-screen bg-white">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-zinc-500 mb-6">
          <Link to="/browse" className="hover:text-[#002FA7] flex items-center gap-1" data-testid="back-to-browse">
            <ChevronLeft className="w-4 h-4" /> Browse
          </Link>
          <span>/</span>
          <Link to={`/browse?category=${listing.category}`} className="hover:text-[#002FA7] capitalize">{listing.category?.replace('_', ' ')}</Link>
          {listing.subcategory && (
            <>
              <span>/</span>
              <span>{listing.subcategory}</span>
            </>
          )}
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Images */}
          <div className="lg:col-span-3" data-testid="listing-images">
            <div className="bg-zinc-100 rounded-lg overflow-hidden aspect-[4/3] relative">
              <img src={images[selectedImg]} alt={listing.title} className="w-full h-full object-cover" />
              {listing.promoted && (
                <span className="absolute top-4 left-4 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-[#002FA7] text-white gap-1">
                  <Zap className="w-3 h-3" /> Featured
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImg(i)}
                    className={`w-20 h-20 rounded-md overflow-hidden flex-shrink-0 border-2 transition-colors ${i === selectedImg ? 'border-[#002FA7]' : 'border-transparent'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-2 space-y-6" data-testid="listing-details">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mb-2" data-testid="listing-title">{listing.title}</h1>
              {listing.listing_type !== 'lost_found' && listing.listing_type !== 'announcement' ? (
                <p className="text-3xl font-black text-[#002FA7]" data-testid="listing-price">
                  {listing.price === 0 ? 'Free' : `$${listing.price?.toFixed(2)}`}
                  {listing.listing_type === 'rental' && listing.price > 0 && <span className="text-base font-medium text-zinc-400">/mo</span>}
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-3 text-sm text-zinc-500">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {listing.city}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {timeAgo(listing.created_at)}</span>
              <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {listing.views} views</span>
            </div>

            {listing.listing_type !== 'lost_found' && listing.listing_type !== 'announcement' && (
            <div className="flex gap-2">
              {listing.payment_method === 'stripe' || listing.payment_method === 'both' ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><CreditCard className="w-3 h-3 mr-1" /> Online Payment</span>
              ) : null}
              {listing.payment_method === 'cash' || listing.payment_method === 'both' ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"><Banknote className="w-3 h-3 mr-1" /> Cash / Meet-up</span>
              ) : null}
              {listing.condition && listing.listing_type !== 'event' && listing.listing_type !== 'job' && listing.listing_type !== 'service' && listing.listing_type !== 'rental' && listing.listing_type !== 'real_estate' && listing.listing_type !== 'garage_sale' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-800 capitalize">{listing.condition}</span>
              )}
            </div>
            )}

            <div className="border-t border-zinc-200 pt-6">
              <h3 className="font-bold text-zinc-900 mb-2">Description</h3>
              <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-wrap" data-testid="listing-description">{listing.description}</p>
            </div>

            {/* Type-Specific Details */}
            {listing.type_fields && Object.keys(listing.type_fields).length > 0 && (
              <TypeDetails listing={listing} />
            )}

            {/* Seller Card */}
            <div className="border border-zinc-200 rounded-lg p-4" data-testid="seller-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-zinc-200 flex items-center justify-center text-lg font-bold text-zinc-500 flex-shrink-0 overflow-hidden">
                  {seller.avatar ? <img src={seller.avatar} alt="" className="w-12 h-12 rounded-full object-cover" /> : seller.name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <Link to={`/profile/${seller.user_id}`} className="font-bold text-zinc-900 hover:text-[#002FA7]" data-testid="seller-name-link">
                    {seller.name}
                    {seller.verified && <ShieldCheck className="w-4 h-4 text-[#002FA7] inline ml-1" />}
                  </Link>
                  <div className="flex items-center gap-2 text-sm text-zinc-500">
                    <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-500 fill-amber-500" /> {seller.rating?.toFixed(1) || '0.0'}</span>
                    <span>({seller.reviews_count || 0} reviews)</span>
                    <span><MapPin className="w-3 h-3 inline" /> {seller.city}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            {!isOwner && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text" placeholder="Hi, is this still available?"
                    value={message} onChange={e => setMessage(e.target.value)}
                    className="flex-1 border border-zinc-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    data-testid="message-input"
                  />
                </div>
                <button
                  onClick={handleSendMessage} disabled={sendingMsg || !message.trim()}
                  className="w-full bg-[#002FA7] text-white hover:bg-[#002585] transition-colors rounded-full py-3 font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="send-message-btn"
                >
                  <MessageCircle className="w-4 h-4" /> {sendingMsg ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            )}

            {isOwner && (
              <div className="space-y-3">
                <Link to={`/promote/${listing.listing_id}`}
                  className="w-full bg-[#002FA7] text-white hover:bg-[#002585] transition-colors rounded-full py-3 font-bold text-sm flex items-center justify-center gap-2"
                  data-testid="promote-listing-btn"
                >
                  <Zap className="w-4 h-4" /> Promote this Listing
                </Link>
              </div>
            )}

            {/* Report */}
            <div className="flex gap-3 text-sm">
              <Dialog>
                <DialogTrigger asChild>
                  <button className="text-zinc-400 hover:text-red-500 flex items-center gap-1 transition-colors" data-testid="report-listing-btn">
                    <Flag className="w-4 h-4" /> Report
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Report this listing</DialogTitle></DialogHeader>
                  <div className="space-y-4 pt-4">
                    <select value={reportReason} onChange={e => setReportReason(e.target.value)}
                      className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm" data-testid="report-reason-select">
                      <option value="">Select a reason</option>
                      <option value="scam">Scam or fraud</option>
                      <option value="prohibited">Prohibited item</option>
                      <option value="inappropriate">Inappropriate content</option>
                      <option value="spam">Spam</option>
                      <option value="other">Other</option>
                    </select>
                    <button onClick={handleReport} disabled={!reportReason}
                      className="w-full bg-red-500 text-white rounded-full py-2.5 font-bold text-sm disabled:opacity-50"
                      data-testid="submit-report-btn"
                    >Submit Report</button>
                  </div>
                </DialogContent>
              </Dialog>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-zinc-400 hover:text-zinc-600 flex items-center gap-1 transition-colors" data-testid="share-listing-btn">
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 px-2 py-1.5">Share this listing</p>
                  <a
                    href={shareLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-2 py-2 rounded-md text-sm text-zinc-700 hover:bg-zinc-100 transition-colors"
                    data-testid="share-facebook"
                  >
                    <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    Facebook
                  </a>
                  <a
                    href={shareLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-2 py-2 rounded-md text-sm text-zinc-700 hover:bg-zinc-100 transition-colors"
                    data-testid="share-twitter"
                  >
                    <svg className="w-4 h-4 text-zinc-900" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    X (Twitter)
                  </a>
                  <a
                    href={shareLinks.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-2 py-2 rounded-md text-sm text-zinc-700 hover:bg-zinc-100 transition-colors"
                    data-testid="share-whatsapp"
                  >
                    <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </a>
                  <div className="border-t border-zinc-100 my-1" />
                  <button
                    onClick={handleCopyLink}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-md text-sm text-zinc-700 hover:bg-zinc-100 transition-colors"
                    data-testid="share-copy-link"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-zinc-400" />}
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
